import Autosuggest, {
    RenderSuggestionParams,
    RenderSuggestionsContainerParams,
    SuggestionSelectedEventData,
    SuggestionsFetchRequestedParams,
} from 'react-autosuggest';
import { Component, FocusEvent, FormEvent, ReactElement, ReactNode, createRef } from 'react';
import { IconButton, InputAdornment, MenuItem, Popper, TextField, Theme } from '@mui/material';
import { PoolTextField } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { noop } from '@synergy/utils';
import ClearIcon from '@mui/icons-material/Clear';
import parse from 'autosuggest-highlight/parse';

import { match } from './utils/match';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            flexGrow: 1,
            position: 'relative',
            display: 'flex',
        },
        suggestionsContainerOpen: {
            display: 'block',
            position: 'relative',
            top: 4,
            left: 0,
            right: 0,
            marginBottom: 10,

            maxHeight: 350,
            minHeight: 0,
            overflowY: 'auto',
            zIndex: 2, // set to 2 because the headlines that may be underneath the container have zIndex 1

            width: '100%',
            border: '1px solid #F1F1F1',
            backgroundColor: theme.palette.background.default,
            boxShadow:
                '0px 1px 5px 0px rgba(0, 0, 0, 0.2), ' +
                '0px 2px 2px 0px rgba(0, 0, 0, 0.14), ' +
                '0px 3px 1px -2px rgba(0, 0, 0, 0.12)',
            '&::-webkit-scrollbar': {
                width: theme.scrollbars.width,
            },
            '&::-webkit-scrollbar-thumb': {
                background: theme.scrollbars.color,
                borderRadius: theme.scrollbars.borderRadius,
            },
            '&::-webkit-scrollbar-track': {
                background: theme.scrollbars.backgroundColor,
                borderRadius: theme.scrollbars.borderRadius,
            },
        },
        suggestionFooter: {
            position: 'fixed',
            bottom: theme.spacing(),
            left: 0,
            right: theme.spacing(),
            backgroundColor: theme.palette.background.default,
        },
        suggestionFooterListWrapper: {
            '& > ul': {
                marginBottom: 60,
            },
        },
        suggestion: {
            display: 'block',
        },
        suggestionsList: {
            margin: 0,
            padding: 0,
            listStyleType: 'none',
            width: '100%',
            display: 'table',

            ' & > li': {
                display: 'table-row-group',

                '&:last-child > div > div': {
                    borderBottom: 'none',
                },
            },
        },
        highlighted: {
            color: theme.palette.primary.main,
            fontWeight: 'bold',
        },
        suggestionItem: {
            display: 'table-row',
            fontSize: '15px', // screendesign

            '&:hover': {
                backgroundColor: '#F8F8F8',
            },

            '& > div': {
                display: 'table-cell',
                padding: '15px',
            },

            '& > span:last-of-type': {
                display: 'none',
            },
        },
        suggestionActionContainer: {
            '& > div:last-of-type': {
                padding: theme.spacing(2, 0, 0, 0),
            },

            '& > div:last-of-type > div': {
                textAlign: 'right',
                visibility: 'hidden',
            },

            '&:hover > div:last-of-type > div': {
                visibility: 'visible',
            },
        },
        suggestionActions: {
            '& svg': {
                color: theme.palette.text.primary,
                fontSize: '18px',
            },
        },
        inputDisabled: {
            cursor: 'not-allowed',
            opacity: 0.38,
        },
        readOnly: {
            // TODO: move to theme
            backgroundColor: '#FBFBFB',
            border: 'none',
            borderRadius: '4px',
            '& > div': {
                border: '1px solid rgba(33, 39, 46, .27)',
                opacity: 0.6,
                color: theme.palette.text.primary,
            },
            '& > label': {
                color: theme.palette.text.primary,
                opacity: 0.6,
            },
        },
    });

interface PoolAutocompleteListProps<T extends BaseAutocompleteSuggestionType> extends WithStyles<typeof styles> {
    data: T[];
    order?: string[];
    exclude?: string[];
    getSuggestionValue: (suggestion: T) => string;
    getDisplayValue?: (suggestion: T) => string;
    handleSuggestionSelected?: (data: T | null) => void;
    onValueChanged?: (value: string) => void;
    placeholder?: string;
    label?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: JSX.Element | null;
    clearInputOnSelect?: boolean;
    footer?: ReactNode;
    initialValue?: string;
    initialSuggestionValue?: T;
    readOnly?: boolean;
    inputRef?: (ref: HTMLInputElement) => void;
    disablePreventOverflow?: boolean;
    adornment?: ReactNode;
    alwaysShowSuggestions?: boolean;
}

interface PoolAutocompleteListState<T> {
    value: string;
    suggestions: T[];
}

export interface BaseAutocompleteSuggestionType {
    id: string | number;
    // Generic index signature
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [property: string]: any;
    actions?: ReactElement<ReactNode>;
}

// FIXME shouldn't lose generic type when wrapping component in withStyles
export const PoolAutocompleteList = withStyles(styles)(
    class PoolAutocompleteListBase<T extends BaseAutocompleteSuggestionType> extends Component<
        PoolAutocompleteListProps<T>,
        PoolAutocompleteListState<T>
    > {
        private rootNode = createRef<Autosuggest<T, T>>();

        public getDisplayValue = (props: PoolAutocompleteListProps<T>) => {
            if (props.initialSuggestionValue) {
                if (this.props.getDisplayValue) {
                    return this.props.getDisplayValue(props.initialSuggestionValue);
                }
                const suggestion = this.props.getSuggestionValue(props.initialSuggestionValue);
                if (suggestion) {
                    return suggestion;
                }
            }
            return props.initialValue || '';
        };

        public override state: PoolAutocompleteListState<T> = {
            value: this.getDisplayValue(this.props),
            suggestions: [],
        };

        private hasActions = () => {
            let found = false;

            this.props.data.forEach(item => {
                Object.keys(item).forEach(key => {
                    if (key === 'action' || key === 'actions') {
                        found = true;
                    }
                });
            });

            return found;
        };

        // Tell Autosuggest how to search for suggestions
        public getSuggestions = (value: string, props: PoolAutocompleteListProps<T> = this.props) => {
            const inputValue = value ? value.trim().toLowerCase() : '';

            if (props.onValueChanged) {
                props.onValueChanged(inputValue);
                return props.data;
            }

            if (inputValue.length === 0) {
                return props.data;
            }

            return props.data.filter(data => {
                let found = false;
                const keys: (keyof T)[] = Object.keys(data);
                keys.forEach(key => {
                    // Convert all numbers to strings to make them searchable
                    if (typeof data[key] === 'number') {
                        data[key] = data[key].toString();
                    }

                    if (typeof data[key] === 'string' && data[key].toLowerCase().indexOf(inputValue) >= 0) {
                        found = true;
                    }
                });
                return found;
            });
        };

        public renderSuggestionContainer = (options: RenderSuggestionsContainerParams) => {
            const { containerProps, children } = options;

            if (!this.rootNode.current?.input) {
                return null;
            }

            return (
                <Popper
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={[
                        {
                            name: 'preventOverflow',
                            enabled: !this.props.disablePreventOverflow,
                            options: {
                                boundariesElement: 'scrollParent',
                            },
                        },
                        {
                            name: 'flip',
                            enabled: true,
                        },
                        {
                            name: 'hide',
                            enabled: false,
                        },
                    ]}
                    style={
                        this.rootNode.current?.input && {
                            minWidth: this.rootNode.current.input.clientWidth,
                            zIndex: 99999,
                        }
                    }
                    anchorEl={this.rootNode.current.input}
                    open={true}
                    onClick={noop}
                >
                    {() => (
                        <div {...containerProps}>
                            <div className={this.props.footer ? this.props.classes.suggestionFooterListWrapper : ''}>
                                {children}
                            </div>
                            {!!children && this.props.footer && (
                                <div className={this.props.classes.suggestionFooter}>{this.props.footer}</div>
                            )}
                        </div>
                    )}
                </Popper>
            );
        };

        public renderSuggestion = (suggestion: T, params: RenderSuggestionParams) => {
            const menuChildren: ReactNode[] = [];

            const exclude = this.props.exclude || [];
            const order = this.props.order || [];
            const foundKeys = Object.keys(suggestion);
            const sortedKeys: string[] = [];

            order.forEach(key => {
                if (Object.prototype.hasOwnProperty.call(suggestion, key)) {
                    sortedKeys.push(foundKeys.splice(foundKeys.indexOf(key), 1)[0]);
                }
            });

            [...sortedKeys, ...foundKeys]
                .filter(key => !exclude.includes(key))
                .forEach((key, index) => {
                    if (typeof suggestion[key] === 'string') {
                        const matches = match(suggestion[key], params.query);
                        const parts = parse(suggestion[key], matches);
                        menuChildren.push(
                            <div key={key}>
                                {parts.map((part, partIndex) => {
                                    return part.highlight ? (
                                        <span key={partIndex} className={this.props.classes.highlighted}>
                                            {part.text}
                                        </span>
                                    ) : (
                                        <span key={partIndex}>{part.text}</span>
                                    );
                                })}
                            </div>
                        );
                    } else {
                        menuChildren.push(
                            <div
                                key={'action' + index}
                                className={key === 'actions' ? this.props.classes.suggestionActions : ''}
                            >
                                {suggestion[key]}
                            </div>
                        );
                    }
                });

            return (
                <MenuItem
                    key={suggestion.id}
                    selected={params.isHighlighted}
                    component="div"
                    className={
                        !this.hasActions
                            ? this.props.classes.suggestionItem
                            : `${this.props.classes.suggestionItem} ${this.props.classes.suggestionActionContainer}`
                    }
                >
                    {menuChildren}
                </MenuItem>
            );
        };

        public shouldRenderSuggestions = (value: string) => {
            return (value && value.length > 0) || !!this.props.alwaysShowSuggestions;
        };

        private processInputRef = (inputPropsRef: (ref: HTMLInputElement) => void) => (inputRef: HTMLInputElement) => {
            if (this.props.inputRef) {
                this.props.inputRef(inputRef);
            }
            if (inputPropsRef) {
                inputPropsRef(inputRef);
            }
        };

        private handleClear = () => {
            this.setState({
                value: '',
            });

            if (this.props.handleSuggestionSelected) {
                this.props.handleSuggestionSelected(null);
            }
        };

        private handleBlur =
            (callback: (event: FocusEvent<HTMLInputElement>) => void) => (event: FocusEvent<HTMLInputElement>) => {
                if (!event.currentTarget.value) {
                    this.setState({ value: '' });
                    if (this.props.handleSuggestionSelected) {
                        this.props.handleSuggestionSelected(null);
                    }
                }
                if (callback) {
                    callback(event);
                }
            };

        // Autosuggest typings are broken, they use own definitions of input props
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public renderInputComponent = (inputProps: any) => {
            const { label, required, disabled, error, classes, ref, ...other } = inputProps;

            return (
                <TextField
                    label={label}
                    required={required}
                    disabled={disabled}
                    error={error}
                    fullWidth={true}
                    InputProps={{
                        classes: { disabled: classes.inputDisabled },
                        inputRef: this.processInputRef(ref),
                        endAdornment: !disabled && (
                            <InputAdornment position="end">
                                {this.props.adornment}
                                <IconButton component="div" onClick={this.handleClear}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        ...other,
                        autoComplete: 'no',
                        onBlur: this.handleBlur(other.onBlur),
                    }}
                    onClick={noop}
                    InputLabelProps={{ disabled }}
                />
            );
        };

        public onSuggestionSelected = (event: FormEvent, data: SuggestionSelectedEventData<T>) => {
            if (this.props.clearInputOnSelect) {
                this.setState({
                    value: '',
                });
            }

            if (this.props.handleSuggestionSelected) {
                this.props.handleSuggestionSelected(data.suggestion);
            }
        };

        public onChange = (_event: FormEvent<HTMLElement>, changeEvent: Autosuggest.ChangeEvent) => {
            this.setState({ value: changeEvent.newValue });
        };

        public onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
            this.setState({ suggestions: this.getSuggestions(params.value) });
        };

        public onSuggestionsClearRequested = () => {
            this.setState({ suggestions: [] });
        };

        public override UNSAFE_componentWillReceiveProps(props: PoolAutocompleteListProps<T>) {
            const partialState: Pick<PoolAutocompleteListState<T>, 'suggestions' | 'value'> =
                // Enforce correct typings
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                { suggestions: props.data } as any;

            if (
                props.initialSuggestionValue !== this.props.initialSuggestionValue ||
                props.initialValue !== this.props.initialValue
            ) {
                partialState.value = this.getDisplayValue(props);
            }

            this.setState(partialState);
        }

        public override render() {
            const { suggestions, value } = this.state;
            const { classes, readOnly, label, placeholder } = this.props;

            const inputProps = {
                value,
                placeholder,
                label,
                required: this.props.required,
                disabled: this.props.disabled,
                onChange: this.onChange,
                error: this.props.error,
                readOnly,
                classes,
            };

            return readOnly ? (
                <PoolTextField
                    readOnly={readOnly}
                    fullWidth={true}
                    label={label}
                    placeholder={placeholder}
                    value={value}
                    endAdornment={
                        this.props.adornment && <InputAdornment position="end">{this.props.adornment}</InputAdornment>
                    }
                />
            ) : (
                <Autosuggest
                    ref={this.rootNode}
                    theme={{
                        container: classes.container,
                        suggestionsContainerOpen: classes.suggestionsContainerOpen,
                        suggestionsList: classes.suggestionsList,
                        suggestion: classes.suggestion,
                    }}
                    suggestions={suggestions}
                    onSuggestionSelected={this.onSuggestionSelected}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    getSuggestionValue={this.props.getSuggestionValue}
                    renderSuggestionsContainer={this.renderSuggestionContainer}
                    renderSuggestion={this.renderSuggestion}
                    renderInputComponent={this.renderInputComponent}
                    inputProps={inputProps}
                    shouldRenderSuggestions={this.shouldRenderSuggestions}
                />
            );
        }
    }
);
